import {observable, action, computed, makeObservable} from 'mobx'
import type {GridReadyEvent, GridApi, ColumnState, ColDef} from 'ag-grid-community'
import {isDefined} from '@oc/utils'

/**
 * Base data object that is saved in local storage and includes basic setings for ag grid.
 * In case of need some extra data/properties stored, new interface can extend from this one.
 */
export interface OcAgGridBasicLocalStorageItem {
    /**
     * Includes information about quick filter, and column filters
     */
    filterModel: any // object from gridApi.getFilterModel()
    /**
     * Text value of the quick filter
     */
    quickFilterText: string
    /**
     * State of all columns - includes size settings, order of columns,...
     */
    columnStates: ColumnState[]
    /**
     * If set to True, columns should not be resized automatically (by this.gridApi.sizeColumnsToFit())
     */
    disableAutoSize: boolean
}

interface OcAgGridColumnMenuOption {
    columnId: string
    label: string
    hide: boolean
}

/**
 * Base abstract class that can be extended when implementing view state/model, that is using ag grid.
 * Already includes some functions needed to work with AG grid, like init and dispose GridApi or read and save grid settings to local storage.
 * Action, to be done to use it:
 *  1. Extend your state class from this class:
 *      1. start using the existing properties and functions like quickFilter, onQuickFilterChanged, initGrid, ...
 *      2. implement absract functions
 *  2. Use the hook 'useAgGridLocalStorage' in the context or in root component and pass the state class. It has to be called before hook which cleanes up AgGrid from the state.
 *  3. Start using function 'onQuickFilterChanged' on all places where quick filter is changed.
 *  4. Start using function 'sizeColumnsToFit' instead of using it directly from gridApi, to consider settings for disabled auto sizing.
 *  5. Add this property to OcAgGrid - 'onDisplayedColumnsChanged={state.sizeColumnsToFit}' - it will ensure columns are resized when reseting to default or while hiding columns while _disableColumnAutoSize=false
 *  6. Use the predefined buttons to control the settings:
 *      1. OcAgGridManageVisibleColumnsButton - for managing visible columns and enabling/disabling column auto resizing
 *      2. OcAgGridResetTableSettingsButton - for reseting table to default settings and deleting item in local storage
 */
export abstract class OcAgGridBaseStateWithHelpers<
    T extends OcAgGridBasicLocalStorageItem = OcAgGridBasicLocalStorageItem
> {
    @observable public gridApi: GridApi | undefined
    @observable protected agGridHiddenColumns = new Map<string, boolean>()
    @observable protected _quickFilter = ''
    /**
     * Holds data loaded from local storage
     *
     * - `undefined` = not loaded yet
     * - `null` = loaded, but no local storage data was found
     * - otherwise = actual loaded local storage data
     */
    @observable protected _localStorageData: T | null | undefined = undefined
    @observable protected _disableColumnAutoSize = false
    @observable protected nonAccessibleColumns: string[] = []
    private _username?: string

    /**
     * Public getter for the quickfilter value.
     */
    @computed
    public get quickFilter() {
        return this._quickFilter
    }

    /**
     * Information if both AG grid APIs are already initiated.
     */
    @computed
    public get apiReady() {
        return !!this.gridApi
    }

    /**
     * Information if the columns in the table should be resized automatically.
     */
    @computed
    public get disableColumnAutoSize() {
        return this._disableColumnAutoSize
    }

    /** Username of currently logged in user */
    @computed
    public get username(): string {
        if (!this._username) {
            throw new Error('Accessing username while it is not set yet')
        }
        return this._username
    }

    @computed
    private get storageKey() {
        return `${this.localStorageItemName}-${this.username}`
    }

    /**
     * List of available columns, with the information if the columns is currently hidden or not.
     * It is used to render the user menu, to select which columns should be hidden.
     */
    @computed
    public get columnMenuOptions(): OcAgGridColumnMenuOption[] {
        const colDefs = this.gridApi?.getColumnDefs()
        return Array.from(this.agGridHiddenColumns.entries()).map(item => {
            const menuOption: OcAgGridColumnMenuOption = {
                columnId: item[0],
                label: colDefs?.find(column => (column as ColDef).colId === item[0])?.headerName || item[0],
                hide: item[1]
            }

            return menuOption
        })
    }

    /**
     * Function to perform extra actions, after the settings was retrieved from local storage.
     * For example it can be info about displaying/hiding inactive objects,...
     * If not extra actions are needed, just do empty implementation
     * @param localStorageData data saved in local storage
     */

    protected abstract applyLocalStorageDataCustomActions(localStorageData: T): void

    /**
     * Function to perform extra actions, after the table was reset to default settings.
     * For example it can be setting default value for displaying/hiding inactive objects,...
     * If not extra actions are needed, just do empty implementation
     */
    protected abstract restoreDefaultTableSettingsCustomActions(): void

    /**
     * Function to add extra data to the object, that will be stored in local storage.
     * If no extra data needed, just return the baseData
     * @param baseData basic data to enrich
     */
    protected abstract getLocalStorageDataToSaveCustomData(baseData: OcAgGridBasicLocalStorageItem): T

    /**
     * Function to create the object with all the settings, that will be saved in local storage.
     * It is using also getLocalStorageDataToSaveCustomData to get the extra data.
     * @returns object that will be stored in local storage
     */
    protected getLocalStorageDataToSave(): T {
        const basicData: OcAgGridBasicLocalStorageItem = {
            filterModel: this.gridApi?.getFilterModel(),
            quickFilterText: this._quickFilter,
            columnStates: this.gridApi?.getColumnState() || [],
            disableAutoSize: this._disableColumnAutoSize
        }
        return this.getLocalStorageDataToSaveCustomData(basicData)
    }

    /**
     * @param localStorageItemName name for the item in local storage
     * @param nonAccessibleColumns list of columns (colIds), that are not allowed to be displayed by user, for example columns used only for filtering
     */
    public constructor(protected readonly localStorageItemName: string, nonAccessibleColumns?: string[]) {
        if (nonAccessibleColumns) {
            this.nonAccessibleColumns = nonAccessibleColumns
        }

        makeObservable(this)
    }

    /**
     * Function to save latest settings to the local storage.
     *
     * This function is used during cleanup of hook `useAgGridLocalStorage` and just before AgGrid is being destroyed.
     */
    @action.bound
    public saveAgGridState(): void {
        if (this.gridApi && typeof this._localStorageData !== 'undefined') {
            const newData = this.getLocalStorageDataToSave()
            window.localStorage.setItem(this.storageKey, JSON.stringify(newData))
        }
    }

    /**
     * Function to resize columns automatically. If automatic column resize is disabled, no action is done.
     * This function should be used on all places, where the automatic resize is required, instead of using the API from AG grid events.
     */
    @action.bound
    public sizeColumnsToFit(): void {
        if (this._disableColumnAutoSize) {
            return
        }

        this.gridApi?.sizeColumnsToFit()
    }

    @action.bound
    public initGrid(event: GridReadyEvent): void {
        this.gridApi = event.api

        this.gridApi.addEventListener('gridPreDestroyed', this.handleGridPreDestroyed)

        if (typeof this._localStorageData !== 'undefined') {
            this.applyLocalStorageData()
        }
    }

    @action.bound
    public disposeGrid(): void {
        this.gridApi = undefined
    }

    /**
     * Function to load the settings from local storage.
     */
    @action.bound
    public restoreDataFromLocalStorage(loggedInUsername: string): void {
        this._username = loggedInUsername
        const gridLocalSave = window.localStorage.getItem(this.storageKey)
        this._localStorageData = gridLocalSave ? (JSON.parse(gridLocalSave) as T) : null

        if (this.apiReady) {
            this.applyLocalStorageData()
        }
    }

    /**
     * Base function to apply settings from local storage.
     * It is executed after Grid APIs are ready and local storage was loaded.
     * At the end the function this.applyLocalStorageDataCustomActions is executed to perform custom actions.
     */
    protected applyLocalStorageData() {
        if (!this.gridApi) {
            throw new Error("APIs not ready, this shouldn't happen")
        }

        const colDefs = this.gridApi.getColumnDefs() as ColDef[]
        this.agGridHiddenColumns.clear()

        if (this._localStorageData) {
            // Add columns which were in saved state
            this._localStorageData.columnStates.forEach(column => {
                if (column.colId && !this.nonAccessibleColumns.includes(column.colId)) {
                    this.agGridHiddenColumns.set(column.colId, !!column.hide)
                }
            })

            // Add columns which weren't in saved state
            const columnsFromStorage = new Set(this._localStorageData.columnStates.map(x => x.colId).filter(isDefined))
            colDefs.forEach(column => {
                if (
                    column.colId &&
                    !columnsFromStorage.has(column.colId) &&
                    !this.nonAccessibleColumns.includes(column.colId)
                ) {
                    this.agGridHiddenColumns.set(column.colId, !!column.initialHide)
                }
            })

            this._disableColumnAutoSize = this._localStorageData.disableAutoSize

            this.gridApi.setFilterModel(this._localStorageData.filterModel)
            this.gridApi.applyColumnState({state: this._localStorageData.columnStates, applyOrder: true})

            this.applyLocalStorageDataCustomActions(this._localStorageData)
        } else {
            // No previously saved state, add all columns
            colDefs.forEach(column => {
                if (column.colId && !this.nonAccessibleColumns.includes(column.colId)) {
                    this.agGridHiddenColumns.set(column.colId, !!column.initialHide)
                }
            })
        }

        this.onQuickFilterChanged(this._localStorageData?.quickFilterText ?? '')
        this.sizeColumnsToFit()
    }

    /**
     * Basic actions to restore the default settings.
     * At the end the function 'restoreDefaultTableSettingsCustomActions' is executed.
     */
    @action.bound
    public restoreDefaultTableSettings(): void {
        this.gridApi?.setFilterModel(null)
        this.gridApi?.resetColumnState()

        window.localStorage.removeItem(this.storageKey)
        this._localStorageData = null
        this.applyLocalStorageData()

        this.sizeColumnsToFit()

        this.restoreDefaultTableSettingsCustomActions()
    }

    /**
     * Function to disable/enable automatic column sizing.
     * @param value new value, True - disabled, False - enabled
     */
    @action.bound
    public toggleDisableColumnAutoSize(): void {
        this._disableColumnAutoSize = !this._disableColumnAutoSize
        if (this._disableColumnAutoSize === false) {
            this.sizeColumnsToFit()
        }
    }

    /**
     * Function to toggle if the column is hidden or visible.
     * @param columnId colId of the column
     * @param hide value for hidden
     */
    @action.bound
    public changeColumnVisibility(columnId: string, hide: boolean) {
        if (!this.nonAccessibleColumns.includes(columnId)) {
            this.agGridHiddenColumns.set(columnId, hide)
            this.gridApi?.setColumnsVisible([columnId], !hide)
        }
    }

    /**
     * Function to change the private property _quickFilter and also update the ag grid
     * @param value new value for the quick filter
     */
    @action.bound
    public onQuickFilterChanged(value: string): void {
        this._quickFilter = value
        this.gridApi?.setGridOption('quickFilterText', this._quickFilter)
        this.onFilterChanged()
    }

    @action.bound
    public onFilterChanged(): void {
        this.gridApi?.onFilterChanged()
    }

    @action.bound
    private handleGridPreDestroyed(): void {
        this.saveAgGridState()
        this.gridApi?.removeEventListener('gridPreDestroyed', this.handleGridPreDestroyed)
        this.gridApi = undefined
    }
}
